// Service for managing test completion and tracking completed assessments

export interface CompletedAssessment {
  id: string
  assessmentId: number | string
  assessmentTitle: string
  assessmentType: string
  driveId: string
  driveTitle: string
  sessionId: string
  candidateEmail: string
  completedAt: string
  status: 'completed' | 'submitted'
  durationMinutes: number
}

class TestCompletionService {
  private readonly STORAGE_KEY = 'completedAssessments'
  private readonly SESSION_STORAGE_KEY = 'testSessions'

  // Normalize assessment identifiers to a stable key: "<driveId>_<assessmentType>"
  private normalizeAssessmentKey(assessmentId: number | string): string {
    const idStr = (assessmentId ?? '').toString()
    if (idStr.startsWith('session-assessment_')) {
      // Format: session-assessment_3_fundamentals-<ts>-<rand>
      const afterPrefix = idStr.substring('session-assessment_'.length)
      const mainPart = afterPrefix.split('-')[0]
      return mainPart
    }
    return idStr
  }

  /**
   * Mark an assessment as completed
   */
  markAssessmentCompleted(data: {
    assessmentId: number | string
    assessmentTitle: string
    assessmentType: string
    driveId: string
    driveTitle: string
    sessionId: string
    candidateEmail: string
    durationMinutes: number
  }): void {
    try {
      const completedAssessment: CompletedAssessment = {
        id: `${data.assessmentId}_${data.candidateEmail}_${Date.now()}`,
        assessmentId: data.assessmentId,
        assessmentTitle: data.assessmentTitle,
        assessmentType: data.assessmentType,
        driveId: data.driveId,
        driveTitle: data.driveTitle,
        sessionId: data.sessionId,
        candidateEmail: data.candidateEmail,
        completedAt: new Date().toISOString(),
        status: 'completed',
        durationMinutes: data.durationMinutes
      }

      // Get existing completed assessments
      const existing = this.getCompletedAssessments()
      
      // Check if this assessment is already completed by this candidate
      const alreadyCompleted = existing.some(
        assessment => 
          assessment.assessmentId === data.assessmentId && 
          assessment.candidateEmail === data.candidateEmail
      )

      if (!alreadyCompleted) {
        // Add to completed assessments
        const updated = [...existing, completedAssessment]
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updated))
        
        // Also store in test sessions for history
        this.storeTestSession(completedAssessment)
        
        //console.log('Assessment marked as completed:', completedAssessment)
      }
    } catch (error) {
      console.error('Failed to mark assessment as completed:', error)
    }
  }

  /**
   * Get all completed assessments for a candidate
   */
  getCompletedAssessmentsForCandidate(candidateEmail: string): CompletedAssessment[] {
    const allCompleted = this.getCompletedAssessments()
    const mine = allCompleted.filter(assessment => assessment.candidateEmail === candidateEmail)
    // De-dup by normalized key, keep latest by completedAt
    const latestByAssessment = new Map<string, CompletedAssessment>()
    for (const item of mine) {
      const key = this.normalizeAssessmentKey(item.assessmentId)
      const prev = latestByAssessment.get(key)
      if (!prev || new Date(item.completedAt).getTime() > new Date(prev.completedAt).getTime()) {
        latestByAssessment.set(key, item)
      }
    }
    return Array.from(latestByAssessment.values())
  }

  /**
   * Check if an assessment is completed by a candidate
   */
  isAssessmentCompleted(assessmentId: number | string, candidateEmail: string): boolean {
    const completed = this.getCompletedAssessments()
    const targetKey = this.normalizeAssessmentKey(assessmentId)
    return completed.some(a => a.candidateEmail === candidateEmail && this.normalizeAssessmentKey(a.assessmentId) === targetKey)
  }

  /**
   * Get all completed assessments
   */
  private getCompletedAssessments(): CompletedAssessment[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      return stored ? JSON.parse(stored) : []
    } catch (error) {
      console.error('Failed to get completed assessments:', error)
      return []
    }
  }

  /**
   * Store test session for history tracking
   */
  private storeTestSession(completedAssessment: CompletedAssessment): void {
    try {
      const existingSessions = this.getTestSessions()
      // De-dup: if a session with same session_id already exists, update it instead of adding
      const alreadyIdx = existingSessions.findIndex(
        (s: any) => s.session_id === completedAssessment.sessionId && s.candidateEmail === completedAssessment.candidateEmail
      )
      const sessionData = {
        id: Date.now(),
        session_id: completedAssessment.sessionId,
        assessment: {
          id: completedAssessment.assessmentId,
          title: completedAssessment.assessmentTitle,
          assessment_type: completedAssessment.assessmentType,
          duration_minutes: completedAssessment.durationMinutes
        },
        status: 'completed',
        duration_minutes: completedAssessment.durationMinutes,
        created_at: completedAssessment.completedAt,
        completed_at: completedAssessment.completedAt,
        driveTitle: completedAssessment.driveTitle,
        candidateEmail: completedAssessment.candidateEmail
      }
      const updated = alreadyIdx >= 0
        ? existingSessions.map((s: any, idx: number) => (idx === alreadyIdx ? sessionData : s))
        : [...existingSessions, sessionData]
      localStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(updated))
    } catch (error) {
      console.error('Failed to store test session:', error)
    }
  }

  /**
   * Get all test sessions
   */
  getTestSessions(): any[] {
    try {
      const stored = localStorage.getItem(this.SESSION_STORAGE_KEY)
      return stored ? JSON.parse(stored) : []
    } catch (error) {
      console.error('Failed to get test sessions:', error)
      return []
    }
  }

  /**
   * Get test sessions for a specific candidate
   */
  getTestSessionsForCandidate(candidateEmail: string): any[] {
    const allSessions = this.getTestSessions()
    // Filter by candidate
    const candidateSessions = allSessions.filter((s: any) => s.candidateEmail === candidateEmail)
    // Sort newest first
    candidateSessions.sort((a: any, b: any) => new Date(b.completed_at || b.created_at).getTime() - new Date(a.completed_at || a.created_at).getTime())
    
    // Keep all sessions - only dedupe by session_id to prevent duplicates of the same session
    const seenSessionIds = new Set<string>()
    const deduped: any[] = []
    for (const s of candidateSessions) {
      const sessionKey = s.session_id
      if (seenSessionIds.has(sessionKey)) continue
      seenSessionIds.add(sessionKey)
      deduped.push(s)
    }
    return deduped
  }

  /**
   * Clear all completed assessments (for testing purposes)
   */
  clearCompletedAssessments(): void {
    localStorage.removeItem(this.STORAGE_KEY)
    localStorage.removeItem(this.SESSION_STORAGE_KEY)
  }

  /**
   * Clear completed assessments for a specific candidate (for debugging)
   */
  clearCompletedAssessmentsForCandidate(candidateEmail: string): void {
    try {
      // Clear completed assessments
      const completed = this.getCompletedAssessments()
      const filteredCompleted = completed.filter(assessment => assessment.candidateEmail !== candidateEmail)
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredCompleted))
      
      // Clear test sessions
      const sessions = this.getTestSessions()
      const filteredSessions = sessions.filter(session => session.candidateEmail !== candidateEmail)
      localStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(filteredSessions))
      
      console.log(`Cleared test data for candidate: ${candidateEmail}`)
    } catch (error) {
      console.error('Failed to clear completed assessments for candidate:', error)
    }
  }
}

export const testCompletionService = new TestCompletionService()
